import React, { useEffect, useState, useCallback } from 'react';
import { db, auth, realtimeDb, storage } from '../firebaseConfig'; // Импорт Firestore, RTDB, Storage и Auth из конфигурации
import { collection, getDocs, doc, getDoc } from 'firebase/firestore';
import { ref as dbRef, get, set } from 'firebase/database';
import { ref as storageRef, uploadBytes, getDownloadURL } from 'firebase/storage';
import { Modal, Button, message, Spin, Alert, Typography } from 'antd';
import { useCompany } from '../contexts/CompanyContext'; // Импортируйте контекст компании
import { SignatureModal } from '../contexts/DocumentSigning'; // Общая модалка канвы подписи
import UmowaCard from '../components/UmowaCard';
import 'antd/dist/reset.css'; // Импорт стилей Ant Design

// Подписи на карточках умов — по типам из канонической модели.
const UMOWA_TYPE_LABELS = {
    zlecenie: 'Umowa zlecenie',
    uop: 'Umowa o pracę',
    b2b: 'B2B',
};

const Dokumenty = () => {
  const [documents, setDocuments] = useState([]);
  const [loading, setLoading] = useState(true); // Состояние для загрузки
  const [error, setError] = useState(null); // Состояние для ошибок
  const [visible, setVisible] = useState(false); // Видимость модального окна
  const [selectedDocument, setSelectedDocument] = useState(null); // Выбранный документ
  const { companyId } = useCompany(); // Получение companyId из контекста

  // --- Umowy из RTDB (новый конвейер партнёрки) ---
  // driverId — ключ-имя водителя из companies/{companyId}/users/{uid},
  // тот же, что в drivers/ (так его резолвят Home.js и Faktury.js).
  const [driverId, setDriverId] = useState(null);
  const [umowy, setUmowy] = useState([]);
  const [umowyLoading, setUmowyLoading] = useState(true);
  const [umowyError, setUmowyError] = useState(null);
  const [umowaToSign, setUmowaToSign] = useState(null); // Умова, для которой открыта модалка подписи

  useEffect(() => {
    const fetchDocuments = async () => {
      try {
        const userId = auth.currentUser?.uid; // Получение текущего пользователя
        if (userId && companyId) { // Проверка на существование companyId
          // Путь к коллекции документов
          const docRef = collection(db, `companies/${companyId}/users/${userId}/umowy`);

          // Получаем все документы в коллекции
          const querySnapshot = await getDocs(docRef);
          const docs = querySnapshot.docs.map(doc => ({
            id: doc.id,
            ...doc.data()
          }));
          setDocuments(docs);
        } else {
          setError("User not authenticated or company ID is missing.");
        }
      } catch (error) {
        setError("Error fetching documents: " + error.message);
      } finally {
        setLoading(false); // Устанавливаем состояние загрузки в false
      }
    };

    fetchDocuments();
  }, [companyId]); // Добавление companyId в зависимости

  const fetchUmowy = useCallback(async () => {
    try {
      const userId = auth.currentUser?.uid;
      if (!userId || !companyId) {
        setUmowyLoading(false);
        return;
      }

      // Имя-ключ водителя лежит в его профиле Firestore — как в остальной панели.
      const userSnap = await getDoc(doc(db, `companies/${companyId}/users`, userId));
      const resolvedDriverId = userSnap.exists() ? userSnap.data().driverId : null;
      if (!resolvedDriverId) {
        // Профиль ещё не связан с водителем — секция просто покажет «brak umów».
        setUmowyLoading(false);
        return;
      }
      setDriverId(resolvedDriverId);

      const snapshot = await get(dbRef(realtimeDb, `companies/${companyId}/umowy/${resolvedDriverId}`));
      const data = snapshot.val() || {};
      const list = Object.entries(data)
        .map(([id, umowa]) => ({ id, ...umowa }))
        // Черновик — внутренняя кухня партнёра, водителю его не показываем.
        .filter((umowa) => umowa.status !== 'szkic')
        // Свежие сверху — тем же порядком партнёрка выбирает активную умову.
        .sort((a, b) => (b.createdAt || '').localeCompare(a.createdAt || ''));
      setUmowy(list);
    } catch (error) {
      console.error('Błąd podczas pobierania umów:', error);
      setUmowyError('Nie udało się załadować umów.');
    } finally {
      setUmowyLoading(false);
    }
  }, [companyId]);

  useEffect(() => {
    fetchUmowy();
  }, [fetchUmowy]);

  // Водителю RTDB разрешает писать ТОЛЬКО узел podpis — статус и pdfUrl не трогаем.
  const handleSaveUmowaSignature = async (blob) => {
    const userId = auth.currentUser?.uid;
    if (!userId || !umowaToSign || !driverId) return;

    try {
      // Storage-правило пускает только в signatures/{свой uid}.
      const signatureFileRef = storageRef(storage, `signatures/${userId}/umowa_${umowaToSign.id}.png`);
      await uploadBytes(signatureFileRef, blob);
      const signatureUrl = await getDownloadURL(signatureFileRef);

      const podpis = { signedAt: new Date().toISOString(), signatureUrl };
      await set(
        dbRef(realtimeDb, `companies/${companyId}/umowy/${driverId}/${umowaToSign.id}/podpis`),
        podpis
      );

      // Запись прошла — обновляем локально, эффективный статус станет «Podpisana».
      setUmowy((prev) => prev.map((u) => (u.id === umowaToSign.id ? { ...u, podpis } : u)));
      setUmowaToSign(null);
      message.success('Umowa została podpisana.');
    } catch (error) {
      console.error('Błąd podczas zapisywania podpisu umowy:', error);
      message.error('Nie udało się zapisać podpisu. Spróbuj ponownie.');
    }
  };

  const handleOpenModal = (doc) => {
    setSelectedDocument(doc);
    setVisible(true);
  };

  // PDF умовы показываем той же модалкой-просмотрщиком, что и легаси-документы.
  const handleOpenUmowaPdf = (umowa) => {
    handleOpenModal({
      title: UMOWA_TYPE_LABELS[umowa.type] || 'Umowa',
      pdfUrl: umowa.pdfUrl,
    });
  };

  const handleDownload = () => {
    if (selectedDocument?.pdfUrl) {
      // Создаем временную ссылку для скачивания
      const link = document.createElement('a');
      link.href = selectedDocument.pdfUrl;
      link.download = selectedDocument.title || 'document.pdf'; // Имя файла для скачивания
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } else {
      message.error('Nie udało się pobrać dokumentu.');
    }
  };

  const handleCancel = () => {
    setVisible(false);
    setSelectedDocument(null);
  };

  if (loading) {
    return <Spin tip="Ładowanie dokumentów..." />; // Индикатор загрузки
  }

  return (
    <div>
      {/* --- Секция «Umowy» (RTDB) — отдельно от легаси-списка Firestore ниже --- */}
      <div style={{ marginBottom: '32px' }}>
        <Typography.Title level={4}>Umowy</Typography.Title>
        {umowyLoading ? (
          <Spin tip="Ładowanie umów..." />
        ) : umowyError ? (
          <Alert message={umowyError} type="warning" showIcon />
        ) : umowy.length > 0 ? (
          umowy.map((umowa) => (
            <UmowaCard
              key={umowa.id}
              umowa={umowa}
              onOpenPdf={handleOpenUmowaPdf}
              onSign={setUmowaToSign}
            />
          ))
        ) : (
          <Alert
            message="Brak umów"
            description="Twoja firma nie przesłała jeszcze żadnej umowy."
            type="info"
            showIcon
          />
        )}
      </div>

      <Typography.Title level={4}>Dokumenty</Typography.Title>
      {error ? (
        <p>{error}</p> // Сообщение об ошибке (только для легаси-списка)
      ) : documents.length > 0 ? (
        documents.map((doc) => (
          <div
            key={doc.id}
            style={{
              marginBottom: '20px',
              border: '1px solid #d9d9d9',
              borderRadius: '12px',
              padding: '20px',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
            }}
          >
            <div>
              <h3 style={{ margin: 0 }}>{doc.title || 'No title available'}</h3> {/* Название документа */}
              <p style={{ margin: '5px 0' }}>Podpisano: {doc.signedAt ? new Date(doc.signedAt.seconds * 1000).toLocaleString('en-GB', { timeZone: 'Europe/Berlin' }) : 'No date available'}</p> {/* Дата подписания */}
            </div>
            <Button type="primary" onClick={() => handleOpenModal(doc)}>
              Zobacz
            </Button>
          </div>
        ))
      ) : (
        <Alert
          message="Brak dostępnych dokumentów"
          description="Nie znaleziono żadnych dokumentów dla tego użytkownika."
          type="info"
          showIcon
        />
      )}

      {selectedDocument && (
        <Modal
          title={selectedDocument.title || 'Dokument'}
          visible={visible}
          onCancel={handleCancel}
          footer={[
            <Button key="download" type="primary" onClick={handleDownload}>
              Pobierz
            </Button>,
            <Button key="cancel" onClick={handleCancel}>
              Zamknij
            </Button>,
          ]}
        >
          <iframe
            src={selectedDocument.pdfUrl}
            width="100%"
            height="500px"
            title="Document Preview"
            style={{ border: 'none' }}
          ></iframe>
        </Modal>
      )}

      {/* Модалка подписи умовы — общая канва из DocumentSigning */}
      <SignatureModal
        open={!!umowaToSign}
        title={umowaToSign ? `Podpisywanie: ${UMOWA_TYPE_LABELS[umowaToSign.type] || 'Umowa'}` : ''}
        onCancel={() => setUmowaToSign(null)}
        onSave={handleSaveUmowaSignature}
      />
    </div>
  );
};

export default Dokumenty;
